import type { ApiError, AuthTokens, Paginated, UploadTicket, VehicleOption } from "../shared/types";
import { canQueueOfflineMutation, enqueueMutation, getQueuedMutations, recordMutationAttempt, removeQueuedMutation, setOfflineQueueScope } from "./offline-queue";

const LEGACY_ACCESS_KEY = "cochegest_access_token";
const REFRESH_KEY = "cochegest_refresh_token";
let accessTokenMemory: string | null = null;
let sessionRevision = 0;
const privateBlobRequests = new Map<string, Promise<Blob>>();

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

export function getAccessToken(): string | null {
  return accessTokenMemory;
}

export function storeTokens(tokens: Pick<AuthTokens, "accessToken" | "user">): void {
  sessionRevision += 1;
  accessTokenMemory = tokens.accessToken;
  setOfflineQueueScope({ userId: tokens.user.id, tenantId: tokens.user.tenant.id });
  localStorage.removeItem(LEGACY_ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export function clearTokens(): void {
  sessionRevision += 1;
  accessTokenMemory = null;
  privateBlobRequests.clear();
  setOfflineQueueScope(null);
  localStorage.removeItem(LEGACY_ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

let refreshPromise: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  const startedRevision = sessionRevision;
  refreshPromise = (async () => {
    const response = await fetch("/api/v1/auth/refresh", {
      method: "POST",
      signal: AbortSignal.timeout(10_000),
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-Cochegest-Client": "browser" },
      body: "{}",
    });
    if (startedRevision !== sessionRevision) return false;
    if (!response.ok) {
      if (response.status !== 401 && response.status !== 403) throw await errorFromResponse(response);
      clearTokens();
      return false;
    }
    const tokens = await response.json() as AuthTokens;
    if (startedRevision !== sessionRevision) return false;
    storeTokens(tokens);
    return true;
  })().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

export async function restoreAccessToken(): Promise<boolean> {
  if (accessTokenMemory) return true;
  return refreshAccessToken();
}

/** No anunciar el cierre hasta que el servidor haya revocado la cookie de sesión. */
export async function logoutSession(): Promise<void> {
  try {
    await apiFetch<void>("/auth/logout", { method: "POST", signal: AbortSignal.timeout(10_000) });
  } catch (error) {
    if (!(error instanceof ApiClientError) || error.status !== 401) throw error;
    if (await refreshAccessToken()) {
      await apiFetch<void>("/auth/logout", { method: "POST", signal: AbortSignal.timeout(10_000) });
    }
    // Refresh rechazado: el servidor ya no reconoce la sesión.
  }
  clearTokens();
}

async function errorFromResponse(response: Response): Promise<ApiClientError> {
  let payload: ApiError | null = null;
  try {
    payload = await response.json() as ApiError;
  } catch {
    // A non-JSON upstream response still becomes a useful client error.
  }
  return new ApiClientError(
    payload?.error.message ?? "No se ha podido completar la operación.",
    response.status,
    payload?.error.code ?? "HTTP_ERROR",
    payload?.error.details,
  );
}

export async function apiFetch<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const headers = new Headers(init.headers);
  const token = getAccessToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const response = await fetch(`/api/v1${path}`, { ...init, headers, credentials: "include" });
  if (response.status === 401 && retry && !path.startsWith("/auth/")) {
    const refreshed = await refreshAccessToken();
    if (refreshed) return apiFetch<T>(path, init, false);
  }
  if (!response.ok) throw await errorFromResponse(response);
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export async function loadAllVehicleOptions(purpose: "sale" | "workshop"): Promise<VehicleOption[]> {
  const items: VehicleOption[] = [];
  let page = 1;
  while (true) {
    const result = await apiFetch<Paginated<VehicleOption>>(
      `/vehicles/options?purpose=${encodeURIComponent(purpose)}&page=${page}&pageSize=100`,
    );
    items.push(...result.items);
    if (items.length >= result.total || result.items.length === 0) return items;
    page += 1;
  }
}

export type OfflineMutationResult<T> = T | { queued: true; offlineId: string };

export async function apiMutation<T>(
  path: string,
  method: "POST" | "PATCH" | "DELETE",
  body: unknown,
): Promise<OfflineMutationResult<T>> {
  const idempotencyKey = crypto.randomUUID();
  try {
    return await apiFetch<T>(path, { method, headers: { "Idempotency-Key": idempotencyKey }, body: JSON.stringify(body) });
  } catch (error) {
    if (error instanceof TypeError || !navigator.onLine) {
      if (!canQueueOfflineMutation(path, method)) {
        throw new ApiClientError("Esta operación necesita conexión y no se ha guardado en el dispositivo.", 0, "OFFLINE_NOT_SUPPORTED");
      }
      const queued = await enqueueMutation({ path, method, body, idempotencyKey });
      return { queued: true, offlineId: queued.id };
    }
    throw error;
  }
}

export async function flushOfflineQueue(): Promise<number> {
  if (!navigator.onLine || !getAccessToken()) return 0;
  const queued = await getQueuedMutations();
  let completed = 0;
  for (const mutation of queued) {
    try {
      await apiFetch(mutation.path, {
        method: mutation.method,
        headers: { "Idempotency-Key": mutation.idempotencyKey },
        body: JSON.stringify(mutation.body),
      });
      await removeQueuedMutation(mutation.id);
      completed += 1;
    } catch (error) {
      if (error instanceof ApiClientError && error.status >= 400 && error.status < 500 && error.status !== 429) {
        await removeQueuedMutation(mutation.id);
        window.dispatchEvent(new CustomEvent("cochegest:offline-conflict", { detail: { path: mutation.path, message: error.message } }));
      } else {
        await recordMutationAttempt(mutation.id);
        break;
      }
    }
  }
  return completed;
}

export async function uploadFile(ticket: UploadTicket, file: File): Promise<void> {
  const response = await fetch(ticket.uploadUrl, {
    method: ticket.method,
    headers: ticket.headers,
    body: file,
  });
  if (!response.ok) throw await errorFromResponse(response);
}

export async function privateObjectUrl(path: string): Promise<string> {
  const token = getAccessToken();
  const requestKey = `${token ?? "cookie"}:${path}`;
  let pending = privateBlobRequests.get(requestKey);
  if (!pending) {
    pending = (async () => {
      const headers = new Headers();
      if (token) headers.set("Authorization", `Bearer ${token}`);
      let response = await fetch(`/api/v1${path}`, { headers, credentials: "include" });
      if (response.status === 401 && await refreshAccessToken()) {
        const refreshed = getAccessToken();
        if (refreshed) headers.set("Authorization", `Bearer ${refreshed}`);
        response = await fetch(`/api/v1${path}`, { headers, credentials: "include" });
      }
      if (!response.ok) throw await errorFromResponse(response);
      return response.blob();
    })();
    privateBlobRequests.set(requestKey, pending);
    void pending.finally(() => {
      if (privateBlobRequests.get(requestKey) === pending) privateBlobRequests.delete(requestKey);
    }).catch(() => undefined);
  }
  // Los consumidores reciben URLs independientes y pueden revocarlas sin
  // romper otra tarjeta que compartió la misma descarga concurrente.
  return URL.createObjectURL(await pending);
}

export async function downloadPrivateFile(path: string, fallbackName: string): Promise<void> {
  const headers = new Headers();
  const token = getAccessToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  let response = await fetch(`/api/v1${path}`, { headers });
  if (response.status === 401 && await refreshAccessToken()) {
    const refreshed = getAccessToken();
    if (refreshed) headers.set("Authorization", `Bearer ${refreshed}`);
    response = await fetch(`/api/v1${path}`, { headers });
  }
  if (!response.ok) throw await errorFromResponse(response);
  const disposition = response.headers.get("content-disposition");
  const name = /filename="?([^";]+)"?/i.exec(disposition ?? "")?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export async function reportClientError(
  source: "error" | "unhandled_rejection" | "react",
  message: string,
): Promise<void> {
  if (!getAccessToken()) return;
  try {
    await apiFetch<void>("/telemetry/client-errors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        route: `${window.location.pathname}${window.location.search}`.slice(0, 300),
        message: message.slice(0, 800),
        source,
        occurredAt: new Date().toISOString(),
      }),
    }, false);
  } catch {
    // Telemetry must never interrupt the user or recursively report itself.
  }
}
